import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Mail, Lock, Eye, Database, AlertCircle } from 'lucide-react';

const Privacy = () => {
  const { t } = useLanguage();

  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: `When you contact us through our website, we collect:
      • Your name and email address
      • Your organization name (if provided)
      • Your message content
      • Technical data (IP address, browser type, device information)`,
    },
    {
      icon: Eye,
      title: 'How We Use Your Information',
      content: `We use your information to:
      • Respond to your inquiries
      • Provide information about our research and services
      • Improve our website and user experience
      • Comply with legal obligations`,
    },
    {
      icon: Lock,
      title: 'Data Security',
      content: `We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is 100% secure.`,
    },
    {
      icon: AlertCircle,
      title: 'Your Rights (GDPR)',
      content: `Under GDPR, you have the right to:
      • Access your personal data
      • Rectify inaccurate data
      • Request erasure of your data
      • Object to data processing
      • Data portability
      • Withdraw consent at any time`,
    },
    {
      icon: Mail,
      title: 'Contact Us',
      content: `For privacy-related questions or to exercise your rights, contact us at:
      Email: privacy@yamai.nl
      Address: Yamai B.V., Breda, Netherlands`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0B1220] pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-yamai-cyan-500/20 to-yamai-purple-500/20 border border-yamai-cyan-400/30 mb-6">
            <Shield className="w-8 h-8 text-yamai-cyan-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('privacy.title')}
          </h1>
          <p className="text-lg text-gray-400">
            {t('privacy.lastUpdated')}: November 24, 2025
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-[#1F2A3A]/50 backdrop-blur-md rounded-2xl border border-white/10 p-8 mb-8">
          <p className="text-gray-300 leading-relaxed">
            {t('privacy.intro')}
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={index}
                className="bg-[#1F2A3A]/50 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:border-yamai-cyan-400/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-yamai-cyan-500/20 to-yamai-purple-500/20 border border-yamai-cyan-400/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-yamai-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-white mb-3">
                      {section.title}
                    </h2>
                    <div className="text-gray-400 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cookies Notice */}
        <div className="mt-12 bg-gradient-to-r from-yamai-cyan-500/10 to-yamai-purple-500/10 border border-yamai-cyan-400/30 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-3">Cookies</h3>
          <p className="text-gray-400 leading-relaxed">
            We use essential cookies to ensure our website functions properly. We do not use advertising or tracking cookies. By using our website, you consent to our use of essential cookies.
          </p>
        </div>

        {/* Updates Notice */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page with an updated "Last Updated" date.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
